import React from "react";
import { useRouteMatch, useHistory, Route } from "react-router";
import { Link } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Instrument, InstrumentType } from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import {
  Container,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Tooltip,
  Fab,
  Typography,
} from "@mui/material";
import {
  Filter as FilterIcon,
  Plus as CreateIcon,
  Pin as PinnedIcon,
  EyeOff as UnpublishedIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import ReviewFormDialog from "components/reviews/ReviewFormDialog";
import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

const { OriginalsType } = Product.displayModes.originalsMouthpiece;

const QUERY = gql`
  query ($first: Int, $after: String, $signatureMouthpiece: ID) {
    reviews(
      first: $first
      after: $after

      signatureMouthpiece: $signatureMouthpiece
    ) {
      edges {
        cursor
        node {
          id
          name

          originals {
            instrument
            instrumentType
            originalsType
          }

          signatureMouthpiece {
            id
            signature

            mouthpieceRef {
              instrument
              instrumentType
            }
          }

          reedsBox {
            instrument
            instrumentType
          }

          ...ReviewFormDialog_review
        }
      }
    }
  }

  ${ReviewFormDialog.fragments.review}
`;

export default function ReviewsPage() {
  const baseMatch = useRouteMatch();
  const history = useHistory();

  const [signatureMouthpieceSearch, setSignatureMouthpieceSearch] =
    React.useState();

  const { data, loading } = useQuery(QUERY, {
    variables: {
      signatureMouthpiece: signatureMouthpieceSearch,
    },
  });

  return (
    <Container fixed>
      <Layout.Toolbar fixed>
        <Stack spacing={2} direction="row" alignItems="center">
          <Tooltip placement="top" title="Créer un avis">
            <Fab
              component={Link}
              to={`${baseMatch.url}/new`}
              size="small"
              color="primary"
            >
              <CreateIcon />
            </Fab>
          </Tooltip>

          <Typography variant="h4" color="primary" sx={{ flexGrow: 1 }}>
            Avis client
          </Typography>

          <Form onSubmit={() => {}}>
            {() => (
              <>
                <SignatureMouthpieceSelectField
                  name="signatureMouthpiece"
                  clearable
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 300 }}
                />

                <OnChange name="signatureMouthpiece">
                  {setSignatureMouthpieceSearch}
                </OnChange>
              </>
            )}
          </Form>
        </Stack>
      </Layout.Toolbar>

      {loading && <Layout.Loading />}

      <Route path={`${baseMatch.path}/:reviewId`}>
        {({ match }) => {
          const reviewId = match?.params.reviewId;

          const review =
            reviewId === "new"
              ? {}
              : data?.reviews.edges.find(({ node }) => node.id === reviewId)
                  ?.node;

          return (
            <ReviewFormDialog
              review={review}
              onClose={() => history.push(baseMatch.url)}
            />
          );
        }}
      </Route>

      <List dense>
        {data?.reviews.edges.map(({ cursor, node }) => (
          <ListItem
            key={cursor}
            button
            component={Link}
            to={`${baseMatch.url}/${node.id}`}
            divider
          >
            <ListItemText
              primary={node.name}
              secondary={
                <>
                  {node.originals && (
                    <Stack component="span" direction="row" spacing="0.5ch">
                      <OriginalsType
                        originalsType={node.originals.originalsType}
                      />
                      <span>-</span>
                      <Instrument instrument={node.originals.instrument} />
                      <InstrumentType
                        instrument={node.originals.instrument}
                        instrumentType={node.originals.instrumentType}
                      />
                    </Stack>
                  )}

                  {node.signatureMouthpiece && (
                    <Stack component="span" direction="row" spacing="0.5ch">
                      <span>{node.signatureMouthpiece.signature}</span>
                      <span>-</span>
                      <Instrument
                        instrument={
                          node.signatureMouthpiece.mouthpieceRef.instrument
                        }
                      />
                      <InstrumentType
                        instrument={
                          node.signatureMouthpiece.mouthpieceRef.instrument
                        }
                        instrumentType={
                          node.signatureMouthpiece.mouthpieceRef.instrumentType
                        }
                      />
                    </Stack>
                  )}

                  {node.reedsBox && (
                    <Stack component="span" direction="row" spacing="0.5ch">
                      <span>Boîte d’anches</span>
                      <span>-</span>
                      <Instrument instrument={node.reedsBox.instrument} />
                      <InstrumentType
                        instrument={node.reedsBox.instrument}
                        instrumentType={node.reedsBox.instrumentType}
                      />
                    </Stack>
                  )}
                </>
              }
            />

            <ListItemSecondaryAction>
              {node.pinned && (
                <Tooltip placement="top" title="Épinglé (sur la home)">
                  <PinnedIcon color="disabled" />
                </Tooltip>
              )}

              {!node.published && (
                <Tooltip placement="top" title="Non publié">
                  <UnpublishedIcon color="disabled" />
                </Tooltip>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Container>
  );
}
